/**
 * nosto-cart-sync — calls `Nosto.reloadCart()` after every cart mutation
 * so Nosto's session-cart (Visitor → Session → Cart panel) AND the
 * cartupdated event always reflect the up-to-date Shopify cart.
 *
 * Pattern (mirrors `theme-projet-x-paris/_dev/js/cart/cart-manager.js`)
 * --------------------------------------------------------------------
 *   Intercept `window.fetch` (and XHR). When the response comes back
 *   from a Shopify cart endpoint (`/cart/add.js`, `/cart/change.js`,
 *   `/cart/update.js`, `/cart/clear.js`, `/cart.js`), wait for the
 *   response promise to resolve (i.e. Shopify has actually persisted the
 *   cart change) and then call `Nosto.reloadCart()` — debounced so a
 *   burst of cart calls (e.g. add + sections refresh) results in one
 *   Nosto sync, not many.
 *
 * Why a fetch interceptor (not the theme's `cart-update` pub/sub or a
 * MutationObserver)
 * ------------------------------------------------------------------
 *   - The theme's `cart-update` pub/sub is published at different points
 *     by different sources:
 *       • `cart-items`   (drawer qty / remove) → after DOM swap ✅
 *       • `product-form` (PLP / SERP / PDP ATC) → BEFORE DOM swap ❌
 *     Subscribing to it made `Nosto.reloadCart()` race ahead of the
 *     section refresh — Lucie's "tagging décalé" complaint.
 *
 *   - A MutationObserver on `#CartDrawer` works for drawer paths but
 *     misses anything that updates the cart without re-rendering the
 *     drawer (e.g. cart-page qty changes).
 *
 *   - Intercepting `fetch`/`XHR` waits for Shopify itself to ack the
 *     mutation. Source-agnostic, DOM-agnostic, future-proof.
 *
 * `Nosto.reloadCart()` doc:
 *   https://docs.nosto.com/shopify/features/add-to-cart
 *
 * Placement attribution (`recommendedProductAddedToCart`)
 * -------------------------------------------------------
 *   When the ATC originates from a Nosto recommendation surface (PDP
 *   recos, sidecart upsell, featured-collections-nosto tab), we ALSO
 *   call `api.recommendedProductAddedToCart(productId, placement)` so
 *   Nosto attributes the conversion to the surface that drove it. The
 *   Nosto.reloadCart() above keeps the session-cart in sync — that
 *   covers the *what* (cart contents). This second call covers the
 *   *where* (which placement converted).
 *
 *   Mirrors `theme-projet-x-paris/_dev/js/components/card-product.js`'s
 *   `trackNostoAddToCart()`. Surface detection: closest ancestor with
 *   `data-nosto-placement`. The same attribute is exposed by
 *   `<nosto-recommendations>` (PDP), `<nosto-recommendations-sidecart>`
 *   (drawer upsell) and `<featured-collection>` (homepage tabs), so a
 *   single `closest('[data-nosto-placement]')` covers all three.
 *
 * Load order
 * ----------
 *   Loaded in `layout/theme.liquid` only when Nosto is enabled. `defer`
 *   is fine here: defer scripts run in document order before
 *   DOMContentLoaded, while cart fetches only happen on user
 *   interaction (long after our hook is in place).
 */
(function () {
	if (typeof window === 'undefined') return;
	if (window.__nostoCartSyncInstalled) return;
	window.__nostoCartSyncInstalled = true;

	const DEBOUNCE_MS = 300;
	// Suffixes (relative to the Shopify-routes root) we treat as a cart
	// MUTATION. Only POSTs to these endpoints trigger a sync.
	//
	// We deliberately don't include `cart` / `cart.js` (the GET endpoint
	// for reading cart state): `Nosto.reloadCart()` itself fetches
	// `/cart.js` internally, so matching it would cause our hook to
	// re-trigger Nosto.reloadCart in a loop (throttled by the debounce
	// but still wasteful).
	const CART_MUTATION_SUFFIXES = ['cart/add', 'cart/change', 'cart/update', 'cart/clear'];

	// Surface attribution captured by the click/submit listeners below.
	// Holds the latest `{productId, placement, ts}` if the ATC came from
	// a Nosto reco surface. Cleared after firing or if older than 30s.
	//
	// 30s is generous: between clicking an ATC card and the cart-add
	// fetch firing, a variant-picker modal can stand open for a while
	// (user reading the variants, hesitating, etc.). Shorter and we'd
	// lose legitimate attributions.
	let pendingAttribution = null;
	const ATTRIBUTION_TTL_MS = 30000;

	let pendingTimer = null;
	function scheduleReload() {
		if (pendingTimer) clearTimeout(pendingTimer);
		pendingTimer = setTimeout(function () {
			pendingTimer = null;

			// Attribution is optional — wrap it so an unexpected failure
			// here never prevents the (mandatory) cart sync below.
			try {
				if (pendingAttribution && Date.now() - pendingAttribution.ts < ATTRIBUTION_TTL_MS) {
					const attr = pendingAttribution;
					pendingAttribution = null;
					if (typeof window.nostojs === 'function') {
						window.nostojs(function (api) {
							const fn = api.recommendedProductAddedToCart || api.reportAddToCart;
							if (typeof fn === 'function') {
								try { fn.call(api, attr.productId, attr.placement); }
								catch (err) { console.warn('[NostoCartSync] attribution failed:', err); }
							}
						});
					}
				} else {
					pendingAttribution = null;
				}
			} catch (err) {
				console.warn('[NostoCartSync] attribution block failed:', err);
			}

			// Cart sync is the load-bearing call — must run even when
			// there's no placement context (regular PDP, drawer qty
			// changes, etc.). Equivalent to projet-x-paris's
			// `#handleNostoCartUpdate()` in cart-manager.js.
			if (window.Nosto && typeof window.Nosto.reloadCart === 'function') {
				Promise.resolve(window.Nosto.reloadCart()).catch(function (err) {
					console.warn('[NostoCartSync] Nosto.reloadCart failed:', err);
				});
			}
		}, DEBOUNCE_MS);
	}

	// Resolve {placement, productId} for an element inside (or equal to)
	// a Nosto surface — `<nosto-recommendations>`,
	// `<nosto-recommendations-sidecart>`, `<featured-collection>`. They
	// all expose `data-nosto-placement` on their root; cards expose
	// `data-nosto-product-id` (sidecart slides) or `data-product-id`
	// (Dawn card-wrappers).
	function resolveAttribution(el) {
		if (!el) return null;
		const surface = el.closest && el.closest('[data-nosto-placement]');
		if (!surface) return null;
		const placement = surface.getAttribute('data-nosto-placement');
		if (!placement) return null;
		let productId = null;
		const slide = el.closest('[data-nosto-product-id]');
		if (slide) productId = slide.getAttribute('data-nosto-product-id');
		if (!productId) {
			const card = el.closest('[data-product-id]');
			if (card) productId = card.getAttribute('data-product-id');
		}
		if (!productId) return null;
		return { productId: String(productId), placement, ts: Date.now() };
	}

	// ── click listener: capture Nosto-surface attribution ─────────────
	// Catches the case where the ATC button opens a variant-picker
	// modal that gets portaled to <body> (so the actual submitting
	// form is no longer a DOM descendant of `<nosto-recommendations>`).
	// Capture-phase + try/catch so a DOM edge case never breaks the
	// fetch interceptor below.
	document.addEventListener('click', function (e) {
		try {
			const attr = resolveAttribution(e.target);
			if (attr) pendingAttribution = attr;
		} catch (_) {}
	}, true);

	// ── submit listener: same idea but tighter — fires when the user
	// submits a cart-add form directly inside a Nosto surface (no
	// modal step), giving us a more precise productId.
	document.addEventListener('submit', function (e) {
		try {
			const form = e.target;
			if (!form || form.tagName !== 'FORM') return;
			const action = form.getAttribute('action') || '';
			if (!/\/cart\/add(?:\.js)?(?:[?#]|$)/.test(action)) return;
			const attr = resolveAttribution(form);
			if (attr) pendingAttribution = attr;
		} catch (_) {}
	}, true);

	// Reads the locale-aware root every call so we work even if the
	// script loads before `Shopify.routes` (defer scripts run after
	// `content_for_header`, but interception happens on user events
	// which fire much later).
	function getRoot() {
		const r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
		return r.replace(/\/+$/, ''); // '/fr/' → '/fr', '/' → ''
	}

	function isCartMutation(url, method) {
		if (!url) return false;
		// Only POST verbs mutate the cart. `Nosto.reloadCart()` does
		// `GET /cart.js` internally — accepting GETs would cause an
		// infinite loop.
		if ((method || 'GET').toUpperCase() !== 'POST') return false;
		try {
			const u = new URL(url, window.location.origin);
			// Same-origin only — don't react to third-party fetches that
			// happen to mention "/cart" in their path.
			if (u.origin !== window.location.origin) return false;
			// Strip `.js` AND trailing slash, then peel the locale root
			// (`Shopify.routes.root`, e.g. `/fr` or `/en-us`). What
			// remains must be exactly one of CART_MUTATION_SUFFIXES,
			// with no further segments — so `/products/foo/cart` or
			// `/cart/notes` don't match.
			const path = u.pathname.replace(/\.js$/, '').replace(/\/+$/, '');
			const root = getRoot();
			if (root && !path.startsWith(root + '/') && path !== root) return false;
			const suffix = root ? path.slice(root.length + 1) : path.replace(/^\//, '');
			return CART_MUTATION_SUFFIXES.indexOf(suffix) !== -1;
		} catch (_) {
			return false;
		}
	}

	// ── fetch interceptor ──────────────────────────────────────────────
	const origFetch = window.fetch;
	window.fetch = function (input, init) {
		const url = typeof input === 'string' ? input : (input && input.url) || '';
		// Method comes from `init.method` for string inputs and from the
		// Request object's own `method` property when a Request is passed.
		const method = (init && init.method) || (input && input.method) || 'GET';
		const promise = origFetch.apply(this, arguments);
		if (isCartMutation(url, method)) {
			promise
				.then(function (response) {
					if (response && response.ok) scheduleReload();
				})
				.catch(function () {});
		}
		return promise;
	};

	// ── XHR interceptor (some legacy theme code paths still use XHR) ──
	const origOpen = XMLHttpRequest.prototype.open;
	const origSend = XMLHttpRequest.prototype.send;
	XMLHttpRequest.prototype.open = function (method, url) {
		try {
			this.__nostoCartUrl = url;
			this.__nostoCartMethod = method;
		} catch (_) {}
		return origOpen.apply(this, arguments);
	};
	XMLHttpRequest.prototype.send = function () {
		const url = this.__nostoCartUrl;
		const method = this.__nostoCartMethod;
		if (url && isCartMutation(url, method)) {
			this.addEventListener('load', function () {
				if (this.status >= 200 && this.status < 300) scheduleReload();
			});
		}
		return origSend.apply(this, arguments);
	};
})();
